/**
 * Pre-limit selection for catalogue price/inventory cron batches.
 * Mirrors listing-domain-sync: only enabled connectors enter the LIMIT window.
 * Claim-time begin_catalogue_domain_sync remains the authoritative gate.
 */

export type CatalogueSyncJobCandidate = {
  id: string;
  connector_id: string | null | undefined;
  created_at: string;
};

/**
 * Filter + order + limit. Disabled-connector jobs are excluded before LIMIT so
 * they cannot starve later jobs for enabled connectors. They stay queued for
 * recovery when the connector is re-enabled (not deleted/failed here).
 */
export function selectEligibleCatalogueSyncJobs<T extends CatalogueSyncJobCandidate>(
  jobs: readonly T[],
  connectors: ReadonlyMap<string, { enabled: boolean }>,
  limit: number,
): T[] {
  const capped = Math.max(0, Math.floor(limit));
  return [...jobs]
    .filter((job) => {
      const connectorId = String(job.connector_id ?? "").trim();
      if (!connectorId) return false;
      return connectors.get(connectorId)?.enabled === true;
    })
    .sort((a, b) => String(a.created_at).localeCompare(String(b.created_at)))
    .slice(0, capped);
}

/**
 * Synchronous per-connector pending guard for Automatic Sync toggles.
 * Rejects a second write for the same id before React re-renders.
 */
export function createConnectorPendingGuard() {
  const pending = new Set<string>();
  return {
    isPending(connectorId: string): boolean {
      return pending.has(connectorId);
    },
    pendingIds(): ReadonlySet<string> {
      return pending;
    },
    async run(
      connectorId: string,
      work: () => Promise<void>,
    ): Promise<"started" | "skipped"> {
      const id = String(connectorId ?? "").trim();
      if (!id || pending.has(id)) return "skipped";
      pending.add(id);
      try {
        await work();
        return "started";
      } finally {
        pending.delete(id);
      }
    },
  };
}

export type ConnectorPendingGuard = ReturnType<typeof createConnectorPendingGuard>;
